import { ref, computed } from 'vue';
import { storeKeywordMapping, getKeywordMappings, deleteKeywordMapping, findMatchingKeywordCategory } from '../utils/keywordService';

// Singleton state - shared across all instances
let isInitialized = false;
const categoryKeywords = ref([]);
const ignoredWords = ref([]);
const isLoading = ref(false);
const error = ref(null);

export function useCategoryKeywords() {

  // Load keywords from API (singleton - only loads once)
  const loadKeywords = async (accessToken) => {
    if (!accessToken) {
      console.warn('useCategoryKeywords: No access token provided');
      return false;
    }

    // Prevent multiple simultaneous loads
    if (isLoading.value) {
      return new Promise((resolve) => {
        const checkLoading = () => {
          if (!isLoading.value) {
            resolve(isInitialized);
          } else {
            setTimeout(checkLoading, 50);
          }
        };
        checkLoading();
      });
    }

    // Skip if already loaded
    if (isInitialized) {
      return true;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const result = await getKeywordMappings(accessToken);
      categoryKeywords.value = result.data.mappings || [];
      isInitialized = true;
      return true;
    } catch (err) {
      error.value = err.message || 'Failed to load keywords';
      console.error('Failed to load keywords:', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // Force reload keywords (useful for manual refresh)
  const reloadKeywords = async (accessToken) => {
    isInitialized = false;
    return await loadKeywords(accessToken);
  };

  // Add a new keyword-category mapping via API
  const addKeyword = async (word, category, accessToken) => {
    if (!word || !category || !accessToken) return false;

    try {
      await storeKeywordMapping(word, category, accessToken);
      // Force reload keywords to sync with API after addition
      await reloadKeywords(accessToken);
      return true;
    } catch (err) {
      error.value = err.message || 'Failed to add keyword';
      console.error('Failed to add keyword:', err);
      return false;
    }
  };

  // Remove a keyword via API
  const removeKeyword = async (word, accessToken) => {
    if (!word || !accessToken) return false;

    try {
      await deleteKeywordMapping(word, accessToken);
      // Force reload keywords to sync with API after deletion
      await reloadKeywords(accessToken);
      return true;
    } catch (err) {
      error.value = err.message || 'Failed to remove keyword';
      console.error('Failed to remove keyword:', err);
      return false;
    }
  };

  // Get category for a keyword
  const getCategoryForKeyword = (word) => {
    if (!word) return null;
    const lowerWord = word.toLowerCase();
    const mapping = categoryKeywords.value.find(m => m.keyword.toLowerCase() === lowerWord);
    return mapping ? mapping.category : null;
  };

  // Add word to ignore list (local only - no API yet)
  const addIgnoredWord = (word) => {
    if (!word) return false;

    const normalizedWord = word.trim().toLowerCase();
    if (ignoredWords.value.includes(normalizedWord)) return false;

    ignoredWords.value.push(normalizedWord);
    return true;
  };

  // Remove word from ignore list (local only)
  const removeIgnoredWord = (word) => {
    const index = ignoredWords.value.indexOf(word);
    if (index > -1) {
      ignoredWords.value.splice(index, 1);
      return true;
    }
    return false;
  };

  // Learn keywords from a description
  const learnFromDescription = async (description, category, accessToken) => {
    if (!description || !category || !accessToken) {
      return { count: 0, keywords: [] };
    }

    // Clean and normalize the entire description as a single keyword
    const normalizedDescription = description.trim().toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (normalizedDescription.length < 3) {
      return { count: 0, keywords: [] };
    }

    // Check if this description is already a keyword
    const exists = categoryKeywords.value.some(
      m => m.keyword.toLowerCase() === normalizedDescription
    );
    if (exists) {
      return { count: 0, keywords: [] };
    }

    try {
      // Store via API
      await storeKeywordMapping(normalizedDescription, category, accessToken);
      // Force reload keywords to sync with API after learning
      await reloadKeywords(accessToken);
      return { count: 1, keywords: [normalizedDescription] };
    } catch (err) {
      error.value = err.message || 'Failed to learn keyword';
      console.error('Failed to learn keyword:', err);
      return { count: 0, keywords: [] };
    }
  };

  // Auto-categorize based on keywords in description
  const autoCategorize = (description) => {
    if (!description || !categoryKeywords.value.length) return null;

    return findMatchingKeywordCategory(description, categoryKeywords.value);
  };

  // Computed property for keyword count
  const keywordCount = computed(() => categoryKeywords.value.length);

  // Computed property for ignored words count
  const ignoredWordsCount = computed(() => ignoredWords.value.length);

  return {
    // Shared singleton state
    categoryKeywords,
    ignoredWords,
    keywordCount,
    ignoredWordsCount,
    isLoading,
    error,
    // Methods
    loadKeywords,
    reloadKeywords,
    addKeyword,
    removeKeyword,
    addIgnoredWord,
    removeIgnoredWord,
    getCategoryForKeyword,
    learnFromDescription,
    autoCategorize
  };
}